import { useState, type FormEvent } from 'react';
import { patchMyTutorProfile } from '@/lib/tutor-api';
import { Label } from '@/components/ui/label';
import { Field, FooterButtons, type StepProps, withCompletedStep, textareaClass } from './shared';

export function BioStep({ profile, accessToken, onSaved, onError }: StepProps) {
  const [bio, setBio] = useState(profile?.bio ?? '');
  const [demoVideoUrl, setDemoVideoUrl] = useState(profile?.demoVideoUrl ?? '');
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (bio.trim().length < 50) {
      onError(new Error('Bio must be at least 50 characters.'));
      return;
    }
    setBusy(true);
    try {
      const next = await patchMyTutorProfile(accessToken, {
        bio,
        demoVideoUrl: demoVideoUrl.trim() === '' ? null : demoVideoUrl.trim(),
        completedSteps: withCompletedStep(profile, 'bio'),
      });
      onSaved(next);
    } catch (err) {
      onError(err);
    } finally {
      setBusy(false);
    }
  };

  const charPct = Math.min(100, Math.round((bio.length / 1500) * 100));

  return (
    <form className="space-y-5" onSubmit={submit}>
      <div className="space-y-2">
        <Label htmlFor="bio">About you</Label>
        <textarea
          id="bio"
          required
          minLength={50}
          maxLength={1500}
          rows={6}
          value={bio}
          onChange={(e) => setBio(e.target.value)}
          placeholder="Tell parents about your teaching background, approach, and what makes you great with students…"
          className={`${textareaClass} min-h-[140px] resize-y`}
        />
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span className={bio.trim().length < 50 ? 'text-amber-600 dark:text-amber-400' : 'text-emerald-600 dark:text-emerald-400'}>
            {bio.trim().length < 50
              ? `${50 - bio.trim().length} more characters needed`
              : 'Minimum met ✓'}
          </span>
          <span>{bio.length} / 1500</span>
        </div>
        <div className="h-1 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-slate-700">
          <div
            className="h-full rounded-full bg-brand-500 transition-all duration-200"
            style={{ width: `${charPct}%` }}
          />
        </div>
      </div>

      <Field
        id="demoVideoUrl"
        label="Demo video URL (optional)"
        type="url"
        value={demoVideoUrl}
        onChange={setDemoVideoUrl}
        helper="A short YouTube or Loom link — parents love seeing you teach before booking."
        placeholder="https://youtube.com/watch?v=..."
      />
      <FooterButtons primaryLabel="Save & continue" busy={busy} />
    </form>
  );
}
